'use client';

/**
 * Version history — the rollback surface.
 *
 * Refine and improve now leave a restore point behind before they overwrite the
 * resume (see `src/lib/resume/versions.ts`). This is where the user gets at
 * them: one list, newest first, saying what each entry is and when it was
 * taken, with a single click to put one back.
 *
 * Deliberately a plain list and not a diff viewer. The user arrives here having
 * just watched the editor repaint with words they did not want; the question is
 * "which one do I want back", not "what exactly differs". The diff is shown
 * *after* restoring, through the same `ResumeComparisonDialog` refine and
 * improve use, so every rewrite in the builder is reported the same way.
 */

import { useCallback, useEffect, useState } from 'react';
import { History, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { cn } from '@/lib/utils';

/** One row of `GET /api/resume-builder/[reportId]/version`. */
export interface ResumeVersionListItem {
  id: string;
  name: string;
  version: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  reason: string | null;
  reasonLabel: string;
  automatic: boolean;
  capturedAt: string;
  restoredFromId: string | null;
  isCurrent: boolean;
}

interface ResumeVersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeId: string;
  /** Called after a successful restore, with what the rollback changed. */
  onRestored: (result: { changes: unknown; restoredLabel: string }) => void;
}

function formatCapturedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';

  const minutesAgo = Math.floor((Date.now() - date.getTime()) / 60_000);
  const stamp = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // A relative hint next to the absolute time: "which of these is the one I
  // just lost" is nearly always a question about the last few minutes.
  if (minutesAgo < 1) return `${stamp} · just now`;
  if (minutesAgo < 60) return `${stamp} · ${minutesAgo} min ago`;
  if (minutesAgo < 60 * 24) return `${stamp} · ${Math.floor(minutesAgo / 60)}h ago`;
  return stamp;
}

export default function ResumeVersionHistoryDialog({
  open,
  onOpenChange,
  resumeId,
  onRestored,
}: ResumeVersionHistoryDialogProps) {
  const [versions, setVersions] = useState<ResumeVersionListItem[]>([]);
  const [autoVersionLimit, setAutoVersionLimit] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const loadVersions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/version`);
      if (!response.ok) {
        throw new Error('Failed to load version history');
      }
      const data = (await response.json()) as {
        versions?: ResumeVersionListItem[];
        autoVersionLimit?: number;
      };
      setVersions(Array.isArray(data.versions) ? data.versions : []);
      setAutoVersionLimit(
        typeof data.autoVersionLimit === 'number' ? data.autoVersionLimit : null
      );
    } catch (loadError) {
      console.error('Failed to load resume versions:', loadError);
      setError('Could not load version history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [resumeId]);

  // Refetched on every open rather than cached: a refine that happened while
  // this dialog was closed must show up, and the list is a handful of rows.
  useEffect(() => {
    if (!open) return;
    void loadVersions();
  }, [loadVersions, open]);

  const handleRestore = useCallback(
    async (version: ResumeVersionListItem) => {
      setRestoringId(version.id);
      setError(null);
      try {
        const response = await fetch(`/api/resume-builder/${resumeId}/version`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ versionId: version.id }),
        });

        const payload = (await response.json().catch(() => null)) as
          | { changes?: unknown; error?: string }
          | null;

        if (!response.ok) {
          setError(payload?.error || 'Could not restore that version.');
          return;
        }

        onRestored({
          changes: payload?.changes,
          restoredLabel: `${version.reasonLabel} · ${formatCapturedAt(version.capturedAt)}`,
        });
        onOpenChange(false);
      } catch (restoreError) {
        console.error('Failed to restore resume version:', restoreError);
        setError('An error occurred while restoring that version.');
      } finally {
        setRestoringId(null);
      }
    },
    [onOpenChange, onRestored, resumeId]
  );

  const restorable = versions.filter((version) => !version.isCurrent);
  const isBusy = restoringId !== null;

  return (
    <Dialog open={open} onOpenChange={(next) => (isBusy ? undefined : onOpenChange(next))}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden p-0">
        <DialogHeading
          title="Version history"
          description="Refine, improve and rollback each save the resume as it was just before they ran. Restore one to put that version back."
        />

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {isLoading ? (
            <p className="flex items-center gap-2 text-body text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading version history...
            </p>
          ) : error ? (
            <p className="text-body text-destructive">{error}</p>
          ) : restorable.length === 0 ? (
            <div className="space-y-1">
              <p className="text-body text-muted-foreground">No earlier versions yet.</p>
              <p className="text-caption text-muted-foreground">
                The next time you refine or improve this resume, the version you have now is saved
                here first.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {restorable.map((version) => (
                <li
                  key={version.id}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-4 py-3',
                    restoringId === version.id && 'opacity-70'
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-label text-foreground">{version.reasonLabel}</p>
                    <p className="truncate text-caption text-muted-foreground">
                      {formatCapturedAt(version.capturedAt)}
                      {!version.automatic && ' · saved by you'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => void handleRestore(version)}
                  >
                    {restoringId === version.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-2 h-4 w-4" />
                    )}
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 border-t border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          {/* The cap is stated rather than applied quietly — history the user
              cannot see disappearing is worse than history that never existed. */}
          <p className="flex items-center gap-2 text-caption text-muted-foreground">
            <History className="h-3.5 w-3.5 shrink-0" />
            {autoVersionLimit
              ? `The last ${autoVersionLimit} automatic versions are kept. Versions you save yourself are always kept.`
              : 'Restoring saves your current resume here first, so a rollback can be undone.'}
          </p>
          <Button type="button" variant="outline" disabled={isBusy} onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
